/* eslint-disable no-unused-vars,@typescript-eslint/no-unused-vars */
import BasePresenter from '@src/js/separate-logic/base-presenter';
import { dispatchProps } from '@src/js/separate-logic/decorate/dispatchProps';
import { reaction, transaction } from 'mobx';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import SnaplineManager from '@editor/src/page/index/components/canvas/snapline-manager';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { external } from '@src/js/separate-logic/decorate/external';
import { checkIsSelectable, getElementRect } from './utils';
import { isLogin } from '@src/js/common/utils';
import { stackTrace } from '@src/slide/infrastructure/module/dependent/taskManager/taskMonitor/taskDecorator';
import Snackbar from '@tencent/dui-mobile/lib/components/Snackbar';
import { isShapeAutoFit } from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust/utils';
import { isIpadApp } from '@src/js/service/enterprise/temp-config';
import i18n from '@i18next/i18n';
import ObserverFactory from '@src/slide/application/service/modelObserver/observerFactory';
import _get from 'lodash/get';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import type PreviewService from '@src/slide/application/service/preview-service';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppEntryType } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { IMobileRightMenuMananger } from '@src/slide/application/service/mobile-right-menu-manager/core/index.interface';

@stackTrace
export default class ElementWrapperPresenter extends BasePresenter {
    private top: number;
    private left: number;
    private height: number;
    private width: number;
    private mouseMove: boolean;
    private mouseDown: boolean;
    private isSelected: boolean;
    private connectingReaction: any;
    private hasWindowMouseUp: boolean;
    private needMouseUp: boolean;
    private preventUpdate: boolean;
    private observer;
    private mobileRightMenuManager: IMobileRightMenuMananger;

    public init(props: any) {
        // 初始化frame
        const size = props.element.props.style;
        this.width = size.width;
        this.height = size.height;
        this.left = size.left;
        this.top = size.top;

        this.mouseDown = false;
        this.mouseMove = false;
        (this.getAppServices() as IMobileEditorAppServices).viewableServiceLayer?.mobileRightMenuMananger?.promise?.()
            .then((mobileRightMenuMananger) => {
                this.mobileRightMenuManager = mobileRightMenuMananger;
            });
    }

    /* 生命周期----------- */

    public componentDidUpdate() {
        this.checkResize();
        this.handleElementUpdate();
    }

    public componentWillUpdate() {
        const isSelectedChanged = this.props.element.selected;
        if (this.isSelected === isSelectedChanged) {
            return;
        }
        this.isSelected = isSelectedChanged;

        this.getAppServices().viewableServiceLayer.editingStateManager.updateElementCropState(false);
    }

    public componentDidMount() {
        this.isSelected = this.props.element.selected;

        this.disposeOnUnmount(reaction(() => {
            const { element } = this.props;
            // 选择状态外部边框frame
            const tableResizing = element.type === UIModelType.TABLE
                && this.getAppServices().viewableServiceLayer.editingStateManager.isResizing;
            const rect = getElementRect({ element, tableResizing, useAdvHas: true  });

            return {
                rect,
            };
        }, () => {
            if (isShapeAutoFit(this.props.element)) {
                this.preventUpdate = true;
            }
            this.forceUpdate();
        }));

        this.observer = ObserverFactory.createObserver(this.props.element);
        if (this.observer) {
            this.observer.addChangesetObserver(this.wrapperForceUpdate);
            this.observer.addLocalChangeObserver(this.wrapperForceUpdate);
        }
    }

    public componentWillUnmount() {
        if (this.observer) {
            this.observer.removeChangesetObserver(this.wrapperForceUpdate);
            this.observer.removeLocalChangeObserver(this.wrapperForceUpdate);
        }
    }

    /* dispatchProps函数---------- */
    @dispatchProps()
    public setPrevent = (prevent) => {
        this.preventUpdate = prevent;
    };

    @dispatchProps()
    public getPrevent = () => this.preventUpdate;

    @dispatchProps()
    public handleElementUpdate = () => {
        if (this.isAnimPreviewing()) {
            // ignore
        } else {
            this.props.onElementUpdate?.();
        }
    };

    /**
     * 按下Editor区域触发，这个时候不会触发element的mouseDown
     */
    @dispatchProps()
    public handleEditorMouseDown = () => {
        // 注册全局up事件，否则鼠标划出element之外，element收不到up事件
        document.addEventListener('mouseup', this.handleDocumentUp);
        this.hasWindowMouseUp = true;
    };

    /**
     * 获取容器的mousedown事件，可能为undefined
     */
    @dispatchProps()
    public getWrapperMouseDown = () => {
        if (this.checkIsDoMouseAction()) {
            return (e) => {
                this.handleMouseDown(this.props.element, e);
                if (!this.props.isGroupChildren) {
                    this.stopEventPropagation(e);
                }
            };
        }
    };

    @dispatchProps()
    public handleMouseDown = (childModel, e) => {
        this.mouseDown = true;

        if (browser && !browser.isMac) {
            SnaplineManager.getInstance().clear(); // bugfix for http://tapd.oa.com/10143861/bugtrace/bugs/view?bug_id=1010143861073980447
        }
        (this.getAppServices() as IPcEditorAppServices).editableServiceLayer?.selectedAnims?.clear();
        transaction(() => {
            if (this.props.isGroupChildren && this.props.handleMouseDown) {
                this.props.handleMouseDown(childModel, e);
            } else {
                e.stopPropagation();
                e.preventDefault();
                // console.log('elementWrapper-mousedown');
                this.getSelectionService().selectedElements.clear(SelectedEleMgrType.GROUP);

                if (this.isSpecialKey(e)) { // shift/control/command按住时多选
                    if (childModel.selected) {
                        this.needMouseUp = true;
                    } else {
                        this.getSelectionService().selectedElements.add(childModel);
                        this.reportOnSelect(childModel);
                    }
                } else if (!childModel.selected) {
                    this.selectElement(childModel);
                } else if (childModel.type === UIModelType.GROUP
                    && this.getSelectionService().selectedElements
                        .selectedArrayForType(SelectedEleMgrType.SLIDE).length > 1) {
                    this.needMouseUp = true;
                }
                if (childModel.type !== UIModelType.TABLE) {
                    this.getAppServices().editableServiceLayer?.dragElementHandler.handleMouseDownOnElement(
                        e,
                        childModel,
                        this.props.getPageScale(),
                        this.needMouseUp,
                        () => {
                            this.needMouseUp = false;
                            this.mobileRightMenuManager?.hideMenu();
                        },
                        () => {
                            this.showIpadAppRightMenu();
                        },
                    );
                }
            }
        });
    };

    @dispatchProps()
    public handleMouseUp = (childModel, e) => {
        (this.getAppServices() as IPcEditorAppServices).editableServiceLayer?.selectedAnims?.clear();
        if (e.nativeEvent.which === 1) {
            this.handleActionEnd(childModel, e);
        }
        if (!this.hasWindowMouseUp) { // 解决鼠标划出Element收不到up事件问题
            const { formatBrushManager, animBrushManager } = (this.getAppServices()).editableServiceLayer || {};
            formatBrushManager?.onElementSelectionMouseUp();
            animBrushManager?.onElementSelectionMouseUp();
        }
        if (!this.mouseMove) {
            dispatchEvent(new Event('changeIframeEvent'));
        }
        this.mouseMove = false;
        this.mouseDown = false;
    };

    @dispatchProps()
    public getWrapperMouseUp = () => {
        if (this.checkIsDoMouseAction()) {
            return e => this.handleMouseUp(this.props.element, e);
        }
    };

    @dispatchProps()
    public getWrapperTouchStart = () => {
        if (this.checkIsDoTouchAction()) {
            return (e) => {
                this.handleTouch(this.props.element, e);
                if (!this.props.isGroupChildren) {
                    this.stopEventPropagation(e);
                }
            };
        }
    };

    @dispatchProps()
    public getWrapperTouchEnd = () => {
        if (this.checkIsDoTouchAction()) {
            return e => this.handleTouchEnd(this.props.element, e);
        }
    };

    @dispatchProps()
    public handleDragDrop = async () => {
        // 保存model，交给copy-paste处理
        if (this.props.element?.selectable) { // 可选择才去替换
            const copyPasteManager = await this.getCopyPasteManager()?.promise();
            if (copyPasteManager) {
                copyPasteManager.savedDropModel = this.props.element;
            }
        }
    };

    @dispatchProps()
    public handleDoubleClick = (e) => {
        if (!isLogin() && this.getUserPriviledgeService().pptEditorState > PPTEditorStateType.EDITABLE) {
            (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
                .viewableServiceLayer.tipsManager.showCannotEditTips();
        }
        this.stopEventPropagation(e);
    };

    @dispatchProps()
    public getHistoryManager() {
        return (this.getAppServices() as IPcEditorAppServices).editableServiceLayer?.historyManager;
    }

    @dispatchProps()
    public handleClick = () => {
        this.showIpadAppRightMenu();
    };

    @dispatchProps()
    public getWrapperMouseMove = () => {
        const state = checkIsSelectable(this.props.element)
            && (!browser.isMobile || browser.isPad);
        if (state) {
            return e => this.handleMouseMove();
        }
    };

    @dispatchProps()
    public checkIsCroping = () => {
        const isSelected = this.props.element.selected;
        return isSelected && this.getAppServices().viewableServiceLayer.editingStateManager.isCroping;
    };

    @dispatchProps()
    public checkIsPreview = () => {
        const isSelected = this.props.element.selected;
        return isSelected
            && (this.isAnimPreviewing()
                || this.getPreviewService()?.isPreview);
    };

    @dispatchProps()
    public checkIsResizing = () => this.getAppServices().viewableServiceLayer.editingStateManager.isResizing;

    @dispatchProps()
    public getMediaManager(): IMediaManager | undefined {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer.mediaManager;
    }

    /* external函数start */

    @external()
    public isShowPlaceholder() {
        return (this.elementRef?.isShowPlaceholder?.()) || false;
    }

    @external()
    public getCloneDom = () => {
        if (this.domRef) {
            const elementRoot = this.domRef.cloneNode(false);
            const cloneDomRefs = this.elementRef.getCloneDom();
            cloneDomRefs?.forEach((element) => {
                element && elementRoot.appendChild(element);
            });
            return elementRoot;
        }
        return null;
    };

    private wrapperForceUpdate = () => {
        this.forceUpdate();
    };

    private get elementRef() {
        return this.getChildRef('elementRef');
    }

    private get domRef() {
        return this.getChildRef('domRef');
    }

    private isSpecialKey = e => e.metaKey || e.shiftKey || e.ctrlKey;

    private isSubGroup = () => this.props.element.type === UIModelType.GROUP && this.props.isGroupChildren;

    private checkIsDoMouseAction = () => checkIsSelectable(this.props.element)
            && (!browser.isMobile || browser.isPad)
            && !this.isSubGroup();

    private checkIsDoTouchAction = () => checkIsSelectable(this.props.element) && !this.isSubGroup();

    private handleTouch = (childModel, e) => {
        if (this.getCurrentTouchable()) return;
        if (this.props.isGroupChildren && this.props.handleMouseDown) {
            this.props.handleMouseDown(childModel, e);
        } else {
            e.stopPropagation();
            e.preventDefault();
            this.getSelectionService().selectedElements.clear(SelectedEleMgrType.GROUP);

            if (!childModel.selected) {
                this.selectElement(childModel);
                this.iframeShowCannotEditTips(childModel);
            } else if (childModel.type === UIModelType.GROUP
                && this.getSelectionService().selectedElements
                    .selectedArrayForType(SelectedEleMgrType.SLIDE).length > 1) {
                this.needMouseUp = true;
            }
        }
    };

    private handleTouchEnd = (childModel, e) => {
        if (this.getCurrentTouchable()) return;
        const { pageX = 0, pageY = 0 } = e.changedTouches?.[0] || {};
        if (!this.mobileRightMenuManager?.isContextMenuShow()) {
            this.mobileRightMenuManager?.showMenu();
        }

        // e.preventDefault();
        this.handleActionEnd(childModel, e);
        dispatchEvent(new Event('changeIframeEvent'));
        this.stopEventPropagation(e);
    };

    private handleMouseMove = () => {
        if (this.mouseDown) {
            this.mouseMove = true;
        }
    };

    private showIpadAppRightMenu() {
        if (isIpadApp() && !this.mobileRightMenuManager?.isContextMenuShow()) {
            this.mobileRightMenuManager?.showMenu();
        }
    }

    /**
     * element刷新时判断是否需要触发resize
     */
    private checkResize = () => {
        const size = this.props.element.props.style;
        if (this.width !== size.width || this.height !== size.height
            || this.left !== size.left || this.top !== size.top) {
            this.width = size.width;
            this.height = size.height;
            this.left = size.left;
            this.top = size.top;
            window.dispatchEvent(new Event('PPT-resize')); // 主动触发window resize
        }
    };

    private handleDocumentUp = (e) => {
        // 在这里触发格式刷up事件
        const { formatBrushManager, animBrushManager } = this.getAppServices().editableServiceLayer || {};
        formatBrushManager?.onElementSelectionMouseUp();
        if (animBrushManager?.animBrushState) {
            animBrushManager?.onElementSelectionMouseUp();
        } else {
            formatBrushManager?.onElementSelectionMouseUp();
        }

        document.removeEventListener('mouseup', this.handleDocumentUp);
        this.hasWindowMouseUp = false;
    };

    private handleActionEnd(childModel, e) {
        if (this.props.isGroupChildren && this.props.handleMouseUp) {
            this.props.handleMouseUp(childModel, e);
        } else if (this.needMouseUp) {
            if (!childModel.selected) {
                this.getSelectionService().selectedElements.add(childModel);
            } else {
                this.getSelectionService().selectedElements.remove(childModel);
            }
            this.needMouseUp = false;
        }
    }

    /**
     * 选中某个元素
     * @param childModel
     */
    private selectElement(childModel) {
        this.getSelectionService().selectedElements.clear();
        this.getSelectionService().selectedElements.add(childModel);
        this.getEditingStateManager().updateElementCropState(false);
        this.reportOnSelect(childModel);
    }

    /**
     * 选中元素后上报
     * @param model
     */
    private reportOnSelect = (model) => {
        if (this.getUserPriviledgeService().pptEditorState !== PPTEditorStateType.EDITABLE) return;
        let action = '';
        if (model.type === UIModelType.SHAPE) {
            action = TDWActionType.CLK_EDIT;
        }
        if (model.type === UIModelType.IMAGE) {
            action = TDWActionType.CLK_IMG;
        }
        if (model.type === UIModelType.GROUP) {
            action = TDWActionType.CLK_COMBIN;
        }
        if (action) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_OBJ,
                action,
            });
        }
    };

    private getCurrentTouchable = () => (browser.isMobile || isIpadApp())
        && isIframeShapeModel(this.props.element)
        && this.props.mobileEditMode() === false;

    private iframeShowCannotEditTips = (model) => {
        if (isIframeShapeModel(model) && this.props.mobileEditMode() && !browser.isMiniProgram) {
            // iframe元素在mobile端编辑态选中，出现弹窗无法编辑
            Snackbar.show({
                message: i18n.t('演示模式支持播放查看。'),
            });
        }
    };

    /**
     * 从 elementRenderHoc.js 迁移而来
     * @param e
     */
    private stopEventPropagation = (e) => {
        if (this.props.fromScale === 1 && !_get(this.props.element, 'props.addon')) {
            // 移动端预览页去掉了蒙版，所以也会收到事件，除了插件没有走SSR需要接收事件其他的直接透传
            return;
        }
        if (this.props.element.selectable) {
            // 阻止元素内部事件向上冒泡
            e?.stopPropagation();
        }
    };

    private getAppServices(): IEditorAppServices {
        return AppServiceProvider.getInstance()
            .getAppServices() as IEditorAppServices;
    }

    private getSelectionService(): ISelectionService {
        return this.getAppServices().viewableServiceLayer.selectionService;
    }

    private getUserPriviledgeService(): IUserPriviledgeService {
        return this.getAppServices().viewableServiceLayer.userPriviledgeService;
    }

    private getPreviewService(): PreviewService | undefined {
        return this.getAppServices().editableServiceLayer?.previewService;
    }

    private getEditingStateManager() {
        return this.getAppServices().viewableServiceLayer.editingStateManager;
    }

    private getCopyPasteManager() {
        return this.getAppServices().editableServiceLayer?.copyPasteManager;
    }

    private isAnimPreviewing() {
        if (this.getAppServices().getEntryType() !== AppEntryType.PC_EDIT) return false;
        return !!(this.getAppServices() as IPcEditorAppServices).viewableServiceLayer
            ?.getEditorAnimPreviewManager()?.isAnimPreviewing;
    }
}
